import React, {useEffect, useState} from "react";
import {CKEditor} from 'ckeditor4-react';
import {AlertCircle, ChevronLeft, ChevronRight, ChevronsRightLeft, Plus, RefreshCw, Search,} from "lucide-react";
import {useNavigate, useSearchParams} from "react-router-dom";
import axios from "axios";
import {useAuth} from "../Context/AuthContext";
import {toast} from "sonner";
import AssignPlanDialog from "../components/AssignPlanDialog";

const Seller = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [sellers, setSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        lastPage: 1,
        perPage: 20,
        total: 0,
    });

    // Separate state for search input (not synced with URL until search button is clicked)
    const [searchInput, setSearchInput] = useState("");

    // Confirm modal state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmOpenDelete, setConfirmOpenDelete] = useState(false);
    const [confirmTarget, setConfirmTarget] = useState(null); // seller object
    const [confirmTargetDelete, setConfirmTargetDelete] = useState(null); // seller object
    const [confirmNextStatus, setConfirmNextStatus] = useState(1); // 1 activate, 0 deactivate
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [confirmLoadingDelete, setConfirmLoadingDelete] = useState(false);

    // Pricing plan dialog state
    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [planDialogSeller, setPlanDialogSeller] = useState(null);

    const openPlanDialog = (seller) => {
        setPlanDialogSeller(seller);
        setPlanDialogOpen(true);
    };

    const closePlanDialog = () => {
        setPlanDialogOpen(false);
        setPlanDialogSeller(null);
    };

    // Bulk email modal state
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkSellerType, setBulkSellerType] = useState("all"); // all | active | inactive
    const [bulkSubject, setBulkSubject] = useState("");
    const [bulkMessage, setBulkMessage] = useState(""); // allow HTML or plain text
    const [bulkLoading, setBulkLoading] = useState(false);

    const navigate = useNavigate();
    const {getToken} = useAuth();

    // Get search term from URL params
    const searchTerm = searchParams.get("search") || "";
    const currentPage = parseInt(searchParams.get("page")) || 1;

    // Initialize search input with URL search term on component mount
    useEffect(() => {
        setSearchInput(searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        const fetchSeller = async () => {
            try {
                setLoading(true);
                // setError(null);

                const token = getToken();
                if (!token) {
                    // setError("No authentication token found");
                    setLoading(false);
                    return;
                }

                // Build query parameters
                const params = new URLSearchParams();
                if (searchTerm) params.append("search", searchTerm);
                if (currentPage > 1) params.append("page", currentPage);

                const response = await axios.get(
                    `https://apisellhub.com/api/super-admin/sellers?${params.toString()}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (response.data.status) {
                    setSellers(response.data.data);
                    setPagination({
                        currentPage: response.data.page_count.current_page,
                        lastPage: response.data.page_count.last_page,
                        perPage: response.data.page_count.per_page,
                        total: response.data.page_count.total,
                    });
                } else {
                    // setError("Failed to load sellers data");
                }
            } catch (err) {
                console.error("Sellers API error:", err);
                if (err.response?.status === 401) {
                    navigate("/center/login", {replace: true});
                    sessionStorage.removeItem("Market-token");
                    toast.error("Token Expired. Please login again.");

                    // setError('Authentication failed. Please login again.');
                    // toast.error(err.response?.data?.errors);
                } else {
                    // setError("Failed to load sellers data");
                    toast.error("Failed to load sellers data");
                }
            } finally {
                setLoading(false);
            }
        };
        fetchSeller().then();
    }, [searchTerm, currentPage, getToken, navigate]);


    const fetchSellers = async () => {
        try {
            setLoading(true);
            // setError(null);

            const token = getToken();
            if (!token) {
                // setError("No authentication token found");
                setLoading(false);
                return;
            }

            // Build query parameters
            const params = new URLSearchParams();
            if (searchTerm) params.append("search", searchTerm);
            if (currentPage > 1) params.append("page", currentPage);

            const response = await axios.get(
                `https://apisellhub.com/api/super-admin/sellers?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data.status) {
                setSellers(response.data.data);
                setPagination({
                    currentPage: response.data.page_count.current_page,
                    lastPage: response.data.page_count.last_page,
                    perPage: response.data.page_count.per_page,
                    total: response.data.page_count.total,
                });
            } else {
                // setError("Failed to load sellers data");
            }
        } catch (err) {
            console.error("Sellers API error:", err);
            if (err.response?.status === 401) {
                navigate("/center/login", {replace: true});
                sessionStorage.removeItem("Market-token");
                toast.error("Token Expired. Please login again.");

                // setError('Authentication failed. Please login again.');
                // toast.error(err.response?.data?.errors);
            } else {
                // setError("Failed to load sellers data");
                toast.error("Failed to load sellers data");
            }
        } finally {
            setLoading(false);
        }
    };

    const openConfirm = (seller) => {
        setConfirmTarget(seller);
        setConfirmNextStatus(seller.status === 1 ? 0 : 1);
        setConfirmOpen(true);
    };

    const openConfirmDelete = (seller) => {
        setConfirmTargetDelete(seller);
        setConfirmOpenDelete(true);
    };


    const closeConfirm = () => {
        if (confirmLoading) return;
        setConfirmOpen(false);
        setConfirmTarget(null);
    };

    const closeConfirmDelete = () => {
        if (confirmLoadingDelete) return;
        setConfirmOpenDelete(false);
        setConfirmTargetDelete(null);
    };

    const handleConfirmToggle = async () => {
        if (!confirmTarget) return;
        try {
            setConfirmLoading(true);
            const token = getToken();
            if (!token) {
                toast.error("No authentication token found");
                return;
            }
            const sellerId = confirmTarget.id;
            await axios.put(
                `https://apisellhub.com/api/super-admin/sellers-status/${sellerId}`,
                {status: confirmNextStatus},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            toast.success(
                confirmNextStatus === 1
                    ? "Seller activated successfully"
                    : "Seller deactivated successfully"
            );
            closeConfirm();
            fetchSellers();
        } catch (err) {
            console.error("Toggle seller status error:", err);
            toast.error(
                err.response?.data?.message || "Failed to update seller status"
            );
        } finally {
            setConfirmLoading(false);
        }
    };
    const handleConfirmToggleDelete = async () => {
        if (!confirmTargetDelete) return;
        try {
            setConfirmLoadingDelete(true);
            const token = getToken();
            if (!token) {
                toast.error("No authentication token found");
                return;
            }
            const sellerId = confirmTargetDelete.id;
            await axios.delete(
                `https://apisellhub.com/api/super-admin/sellers-delete/${sellerId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            toast.success("Seller Deleted successfully");
            closeConfirmDelete();
            fetchSellers();
        } catch (err) {
            console.error("Toggle seller status error:", err);
            toast.error(
                err.response?.data?.message || "Failed to update seller status"
            );
        } finally {
            setConfirmLoadingDelete(false);
        }
    };

    const handleSearch = () => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (searchInput.trim()) {
            newSearchParams.set("search", searchInput.trim());
        } else {
            newSearchParams.delete("search");
        }
        newSearchParams.set("page", "1"); // Reset to first page on search
        setSearchParams(newSearchParams);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const handlePageChange = (page) => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("page", page.toString());
        setSearchParams(newSearchParams);
    };

    const handleSellerLogin = async (id) => {
        try {
            const token = getToken();
            const response = await axios.get(
                `https://apisellhub.com/api/super-admin/seller-login/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response?.data?.status) {
                // window.location.href = `https://dashboard.sellhub.io/auth/signin/${response?.data?.data}`;
                window.open(`https://dashboard.sellhub.io/auth/signin/${response?.data?.data}`, "_blank");
            }
        } catch {

        } finally {

        }
    }

    const getStatusBadge = (status) => {
        if (status === 1) {
            return (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          Active
        </span>
            );
        } else {
            return (
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
          Inactive
        </span>
            );
        }
    };

    // const getPaymentGatewayBadge = (paymentType) => {
    //   if (paymentType === 1) {
    //     return (
    //       <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
    //         MyFatoorah
    //       </span>
    //     );
    //   } else {
    //     return (
    //       <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
    //         Unknown
    //       </span>
    //     );
    //   }
    // };


    // Loading state
    if (loading) {
        return (
            <div className="space-y-6">
                {/* Header Skeleton */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <div className="h-8 bg-gray-600 rounded w-32 mb-2 animate-pulse"></div>
                        <div className="h-4 bg-gray-600 rounded w-64 animate-pulse"></div>
                    </div>
                    <div className="h-10 bg-gray-600 rounded w-32 animate-pulse mt-4 sm:mt-0"></div>
                </div>

                {/* Search Skeleton */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
                    <div
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                        <div className="h-10 bg-gray-600 rounded w-64 animate-pulse"></div>
                        <div className="h-10 bg-gray-600 rounded w-24 animate-pulse"></div>
                    </div>
                </div>

                {/* Table Skeleton */}
                <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                    <div className="p-6 space-y-4">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="flex space-x-4">
                                <div className="h-4 bg-gray-600 rounded w-20 animate-pulse"></div>
                                <div className="h-4 bg-gray-600 rounded w-32 animate-pulse"></div>
                                <div className="h-4 bg-gray-600 rounded w-24 animate-pulse"></div>
                                <div className="h-4 bg-gray-600 rounded w-20 animate-pulse"></div>
                                <div className="h-4 bg-gray-600 rounded w-32 animate-pulse"></div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Loading indicator */}
                {/* <div className="text-center py-8">
          <div className="inline-flex items-center space-x-2 text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
            <span>Loading sellers data...</span>
          </div>
        </div> */}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-white">Seller</h2>
                    <p className="text-gray-400">
                        Manage all seller accounts and their details
                    </p>
                </div>
                <button onClick={() => setBulkOpen(true)}
                        className="flex items-center space-x-2 bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors duration-200 mt-4 sm:mt-0">
                    <Plus className="h-4 w-4"/>
                    <span>Bulk Email</span>
                </button>
            </div>

            {/* Search and Filter */}
            <div className=" rounded-lg border border-gray-700 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="relative flex-1 max-w-md">
                        {/* <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search sellers by name, email, subdomain, or country"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            /> */}
                    </div>

                    <div className="flex items-center space-x-3">
                        <div className="relative flex-1 max-w-md">
                            <Search
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"/>
                            <input
                                type="text"
                                placeholder="Search sellers by name"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                            />
                        </div>
                        <button
                            onClick={handleSearch}
                            className="flex items-center space-x-2 bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
                        >
                            <Search className="h-4 w-4"/>
                            <span>Search</span>
                        </button>

                        <button
                            onClick={() => {
                                setSearchInput("");
                                const newSearchParams = new URLSearchParams(searchParams);
                                newSearchParams.delete("search");
                                newSearchParams.set("page", "1");
                                setSearchParams(newSearchParams);
                            }}
                            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            <RefreshCw className="h-4 w-4"/>
                            <span>Reset</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Sellers Table */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
                <div className="overflow-x-auto no-scrollbar">
                    <table className="min-w-full divide-y divide-gray-700">
                        <thead className="bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                NAME
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                EMAIL
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">
                                SUB DOMAIN
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                COUNTRY
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                WEBSITE
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                STATUS
                            </th>
                            {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  PAYMENT GATEWAY
                </th> */}
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                CREATED AT
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                                ACTION
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-gray-800 divide-y divide-gray-700">
                        {sellers.map((seller) => (
                            <tr
                                key={seller.id}
                                className="hover:bg-gray-700 transition-colors"
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-white">
                                        {seller.name}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-300">{seller.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-white">{seller.subdomain}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-300">
                                        {seller.country}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <a
                                        href={`https://${seller.subdomain}.sellhub.io`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-blue-400 hover:text-blue-300 underline"
                                    >
                                        {seller.subdomain}.sellhub.io
                                    </a>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {getStatusBadge(seller.status)}
                                </td>
                                {/* <td className="px-6 py-4 whitespace-nowrap">
                    {getPaymentGatewayBadge(seller.sellhub_payment_type)}
                  </td> */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-300">
                                        {new Date(seller.created_at).toLocaleDateString("en-US", {
                                            day: "numeric",
                                            month: "short",
                                            year: "numeric",
                                        })}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => openConfirm(seller)}
                                            className="px-3 py-1.5 text-xs font-medium border border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                                        >
                                            {seller.status === 1 ? "Deactivate" : "Activate"}
                                        </button>
                                        <button
                                            onClick={() =>
                                                navigate(`/center/order/${seller.id}`)
                                            }
                                            className="px-3 py-1.5 text-xs font-medium border border-cyan-600 text-cyan-600 rounded-lg hover:bg-cyan-50 transition-colors"
                                        >
                                            Orders
                                        </button>
                                        <button
                                            onClick={() =>
                                                navigate(`/center/audit/${seller.id}`)
                                            }
                                            className="px-3 py-1.5 text-xs font-medium border border-gray-600 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            Audit Log
                                        </button>
                                        <button
                                            onClick={() => openPlanDialog(seller)}
                                            className="px-3 py-1.5 text-xs font-medium border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors"
                                        >
                                            {seller.pricing_plan_id || seller.pricing_plan?.id
                                                ? `Plan: ${seller.pricing_plan?.name ?? "#" + (seller.pricing_plan_id ?? seller.pricing_plan?.id)}`
                                                : "Assign Plan"}
                                        </button>
                                        <button
                                            onClick={() => openConfirmDelete(seller)}
                                            className="px-3 py-1.5 text-xs font-medium border border-red-600 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                                        >
                                            Delete
                                        </button>
                                        <button
                                            onClick={() => {
                                                handleSellerLogin(seller?.id).then()
                                            }}
                                            className="px-3 py-1.5 text-xs font-medium border border-green-600 text-green-600 rounded-lg hover:bg-red-50 transition-colors"
                                        >
                                            LogIn
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-400">
                            Showing {(pagination.currentPage - 1) * pagination.perPage + 1} to{" "}
                            {Math.min(
                                pagination.currentPage * pagination.perPage,
                                pagination.total
                            )}{" "}
                            of {pagination.total} results
                        </div>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                                disabled={pagination.currentPage <= 1}
                                className="px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                            >
                                <ChevronLeft className="h-4 w-4"/>
                                <span>Previous</span>
                            </button>

                            {Array.from(
                                {length: Math.min(5, pagination.lastPage)},
                                (_, i) => {
                                    const pageNum = i + 1;
                                    return (
                                        <button
                                            key={pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                            className={`px-3 py-1 text-sm rounded ${
                                                pageNum === pagination.currentPage
                                                    ? "bg-yellow-600 text-white"
                                                    : "text-gray-400 hover:text-white hover:bg-gray-700"
                                            }`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                }
                            )}

                            <button
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                                disabled={pagination.currentPage >= pagination.lastPage}
                                className="px-3 py-1 text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                            >
                                <span>Next</span>
                                <ChevronRight className="h-4 w-4"/>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirm Modal */}
            {confirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/60"
                        onClick={closeConfirm}
                    ></div>
                    <div
                        className="relative bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md mx-4 p-6 shadow-xl">
                        <div className="flex items-start space-x-3">
                            <AlertCircle className="h-6 w-6 text-yellow-400 mt-0.5"/>
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-1">
                                    Confirm Action
                                </h3>
                                <p className="text-sm text-gray-300">
                                    Are you sure you want to{" "}
                                    {confirmNextStatus === 1 ? "activate" : "deactivate"} this
                                    user?
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center justify-end space-x-3">
                            <button
                                onClick={closeConfirm}
                                disabled={confirmLoading}
                                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                            >
                                No
                            </button>
                            <button
                                onClick={handleConfirmToggle}
                                disabled={confirmLoading}
                                className="px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
                            >
                                {confirmLoading ? "Updating..." : "Yes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete Modal */}
            {confirmOpenDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/60"
                        onClick={closeConfirmDelete}
                    ></div>
                    <div
                        className="relative bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md mx-4 p-6 shadow-xl">
                        <div className="flex items-start space-x-3">
                            <AlertCircle className="h-6 w-6 text-yellow-400 mt-0.5"/>
                            <div>
                                <h3 className="text-lg font-semibold text-white mb-1">
                                    Confirm Delete
                                </h3>
                                <p className="text-sm text-gray-300">
                                    Are you sure you want to delete this user?
                                </p>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center justify-end space-x-3">
                            <button
                                onClick={closeConfirmDelete}
                                disabled={confirmLoadingDelete}
                                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                            >
                                No
                            </button>
                            <button
                                onClick={handleConfirmToggleDelete}
                                disabled={confirmLoadingDelete}
                                className="px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
                            >
                                {confirmLoadingDelete ? "Updating..." : "Yes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Email Modal */}
            {bulkOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60"
                         onClick={() => !bulkLoading && setBulkOpen(false)}></div>
                    <div
                        className="relative bg-gray-800 border border-gray-700 rounded-xl w-full max-w-2xl mx-4 p-6 shadow-xl">
                        <div className="flex items-start justify-between">
                            <h3 className="text-lg font-semibold text-white">Send Email</h3>
                            <button
                                onClick={() => !bulkLoading && setBulkOpen(false)}
                                className="text-gray-400 hover:text-white"
                                aria-label="Close"
                            >
                                <ChevronsRightLeft/>
                            </button>
                        </div>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="block text-sm text-gray-300 mb-1">Seller type <span
                                    className="text-red-500">*</span></label>
                                <select
                                    value={bulkSellerType}
                                    onChange={(e) => setBulkSellerType(e.target.value)}
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg text-white px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                >
                                    <option value="all">All</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-300 mb-1">Subject <span
                                    className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={bulkSubject}
                                    onChange={(e) => setBulkSubject(e.target.value)}
                                    placeholder="Subject"
                                    className="w-full bg-gray-700 border border-gray-600 rounded-lg text-white px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-300 mb-1">Message <span
                                    className="text-red-500">*</span></label>
                                <div className="bg-gray-750 border border-gray-600 rounded-lg">
                                    <CKEditor
                                        initData={bulkMessage}
                                        onChange={(event) => setBulkMessage(event.editor.getData())}
                                        editorUrl="https://cdn.ckeditor.com/4.22.1/standard-all/ckeditor.js"
                                        config={{
                                            height: 250,
                                            removePlugins: 'elementspath',
                                            resize_enabled: false,
                                            toolbar: [
                                                {name: 'clipboard', items: ['Undo', 'Redo']},
                                                {
                                                    name: 'basicstyles',
                                                    items: ['Bold', 'Italic', 'Underline', 'Strike', '-', 'RemoveFormat']
                                                },
                                                {
                                                    name: 'paragraph',
                                                    items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock']
                                                },
                                                {name: 'links', items: ['Link', 'Unlink']},
                                                {name: 'insert', items: ['Table']},
                                                {name: 'styles', items: ['Format']}
                                            ],
                                            versionCheck: false,
                                        }}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-400">HTML will be sent to recipients.</p>
                            </div>
                        </div>
                        <div className="mt-6 flex items-center justify-end space-x-3">
                            <button
                                onClick={() => setBulkOpen(false)}
                                disabled={bulkLoading}
                                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                            >
                                Close
                            </button>
                            <button
                                onClick={async () => {
                                    if (!bulkSubject.trim() || !bulkMessage.trim()) {
                                        toast.error("Subject and Message are required");
                                        return;
                                    }
                                    try {
                                        setBulkLoading(true);
                                        const token = getToken();
                                        if (!token) {
                                            toast.error("No authentication token found");
                                            return;
                                        }
                                        // Prepare body. If plain text, leave as-is. Backend accepts HTML string.
                                        const body = {
                                            subject: bulkSubject.trim(),
                                            message: bulkMessage,
                                            seller_type: bulkSellerType,
                                        };
                                        const resp = await axios.put(
                                            "https://apisellhub.com/api/super-admin/sellers-bulk-email",
                                            body,
                                            {
                                                headers: {
                                                    Authorization: `Bearer ${token}`,
                                                    "Content-Type": "application/json",
                                                },
                                            }
                                        );
                                        const ok = resp.data?.success || resp.data?.status;
                                        if (ok) {
                                            toast.success(resp.data?.message || "Email sent successfully");
                                            setBulkOpen(false);
                                            setBulkSubject("");
                                            setBulkMessage("");
                                            setBulkSellerType("all");
                                        } else {
                                            toast.error(resp.data?.message || "Failed to send email");
                                        }
                                    } catch (err) {
                                        console.error("Bulk email error:", err);
                                        toast.error(err.response?.data?.message || "Failed to send email");
                                    } finally {
                                        setBulkLoading(false);
                                    }
                                }}
                                disabled={bulkLoading}
                                className="px-4 py-2 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
                            >
                                {bulkLoading ? "Sending..." : "Send Email"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Pricing Plan Dialog */}
            <AssignPlanDialog
                open={planDialogOpen}
                seller={planDialogSeller}
                onClose={closePlanDialog}
                onSuccess={() => {
                    closePlanDialog();
                    fetchSellers();
                }}
            />
        </div>
    );
};

export default Seller;
